import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Btn, Card, Label, Input, Textarea, Select } from "@/components/shared";
import { StickyNote, Trash2, Save } from "lucide-react";

export const OFFICE_STATUSES = [
  "", "Invoiced on PPW", "Invoiced – Square", "Invoiced – Found", "Invoiced – Cash App",
  "Invoiced – Cash", "Ready for office", "Returned", "Need more info", "Cancelled", "Other",
];

export function OfficeNotes({ job, setJob }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [status, setStatus] = useState(job.office_status || "");
  const [other, setOther] = useState(job.office_status_other || "");
  const [savingStatus, setSavingStatus] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => api.get(`/jobs/${job.id}/notes`).then((r) => setNotes(r.data)).catch(() => {}), [job.id]);
  useEffect(() => { load(); }, [load]);

  const saveStatus = async (newStatus, newOther) => {
    setSavingStatus(true);
    try {
      await api.put(`/jobs/${job.id}`, { office_status: newStatus, office_status_other: newOther });
      setJob((j) => ({ ...j, office_status: newStatus, office_status_other: newOther }));
      toast.success("Office status updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingStatus(false); }
  };

  const addNote = async () => {
    const t = text.trim();
    if (!t) return;
    setAdding(true);
    try {
      await api.post(`/jobs/${job.id}/notes`, { text: t });
      setText(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  const delNote = async (nid) => {
    try { await api.delete(`/jobs/${job.id}/notes/${nid}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="office-tab">
      <Card className="p-6">
        <Label>Office / Invoice Status</Label>
        <div className="flex flex-wrap items-end gap-3 mt-1">
          <div className="flex-1 min-w-[220px]">
            <Select data-testid="office-status-select" value={status}
              onChange={(e) => { setStatus(e.target.value); if (e.target.value !== "Other") saveStatus(e.target.value, ""); }}>
              {OFFICE_STATUSES.map((s) => <option key={s} value={s}>{s || "— Not set —"}</option>)}
            </Select>
          </div>
          {status === "Other" && (
            <div className="flex-1 min-w-[220px] flex gap-2">
              <Input data-testid="office-status-other" value={other} onChange={(e) => setOther(e.target.value)} placeholder="Describe the status…" />
              <Btn data-testid="office-status-save" disabled={savingStatus} onClick={() => saveStatus("Other", other)}><Save size={14} /></Btn>
            </div>
          )}
        </div>
        <p className="mt-2 font-mono text-[11px] text-zinc-600">Internal office/billing status — not the contractor job status. Clients don't see this.</p>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <StickyNote size={16} className="text-[#FF5722]" />
          <Label className="!mb-0">Private Office Notes (admin only)</Label>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Textarea data-testid="office-note-input" value={text} onChange={(e) => setText(e.target.value)} rows={2}
            placeholder="Log a call, client update or message… (e.g. 'Called PM — approved extra CY, callback Fri')" className="flex-1" />
          <Btn data-testid="office-note-add" disabled={adding || !text.trim()} onClick={addNote}>Add note</Btn>
        </div>
        <div className="mt-4 space-y-2">
          {notes.length === 0 && <div className="font-mono text-xs text-zinc-600">No notes yet.</div>}
          {notes.map((n) => (
            <div key={n.id} data-testid={`office-note-${n.id}`} className="group flex items-start justify-between gap-3 bg-zinc-950 border border-zinc-800 p-3">
              <div className="min-w-0">
                <div className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{n.text}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 mt-1">{n.author_name} · {new Date(n.created_at).toLocaleString()}</div>
              </div>
              <button data-testid={`office-note-del-${n.id}`} onClick={() => delNote(n.id)} className="text-zinc-600 hover:text-red-400 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
